import { useEffect, useRef } from 'react';
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  CheckCircle,
  Database,
  FileText,
  MessageSquare,
  Share2,
  Sparkles,
} from 'lucide-react';
import { trackEvent } from './lib/analytics.js';

const WAITLIST_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSch1Dp9jPn3DVF-kovGGeDQIlM4ewe8ShLYptLYbQDp0NNU3w/viewform?usp=publish-editor';

export default function LandingPage() {
  const brandName = 'Sink';
  const brandBadge = '探せて、育つ。AIナレッジベース。';
  const heroLead = 'スマホからメモを残すだけ。散らばった情報をAIが整理し、アイデアを再発掘。思い付きを資産に変えてみませんか。';

  const thinkRef = useRef(null);
  const sinkRef = useRef(null);
  const syncRef = useRef(null);
  const processRef = useRef(null);
  const finalCtaRef = useRef(null);

  useEffect(() => {
    const refs = [thinkRef, sinkRef, syncRef, processRef, finalCtaRef];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          trackEvent('lp_section_view', { surface: 'lp', section: entry.target.id });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    refs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavClick = (target) => {
    trackEvent('lp_nav_click', { surface: 'lp', target });
    if (target === 'waitlist') {
      trackEvent('generate_lead', { surface: 'lp', method: 'google_form', placement: 'nav' });
    }
  };

  const handleCtaClick = (cta) => {
    trackEvent('lp_cta_click', { surface: 'lp', cta });
    if (cta.startsWith('waitlist')) {
      trackEvent('generate_lead', { surface: 'lp', method: 'google_form', placement: cta });
    }
  };

  const handleLegalClick = (target) => {
    trackEvent('lp_legal_click', { surface: 'lp', target });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-indigo-500/30">
      <nav className="fixed w-full z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-indigo-400">
            <BrainCircuit size={28} />
            <span className="font-bold text-xl tracking-tight text-white">{brandName}</span>
          </div>
          <div className="hidden md:flex gap-8 text-sm font-medium text-slate-400">
            <a href="#sink" onClick={() => handleNavClick('sink')} className="hover:text-white transition">Sink (沈める)</a>
            <a href="#sync" onClick={() => handleNavClick('sync')} className="hover:text-white transition">Sync (繋ぐ)</a>
            <a href="#think" onClick={() => handleNavClick('think')} className="hover:text-white transition">Think (考える)</a>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <a href="/demo" onClick={() => handleNavClick('demo')} className="text-sm font-medium text-slate-300 hover:text-white transition">デモを見る</a>
            <a href={WAITLIST_FORM_URL} onClick={() => handleNavClick('waitlist')} target="_blank" rel="noreferrer" className="text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white px-3 sm:px-4 py-2 rounded-lg transition whitespace-nowrap">
              先行アクセスに登録
            </a>
          </div>
        </div>
      </nav>

      <section className="relative pt-28 pb-16 md:pt-40 md:pb-24 overflow-hidden">

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-transparent to-indigo-500/40" />
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.15em] text-indigo-400 uppercase whitespace-nowrap">
              <Sparkles size={14} className="text-indigo-500 shrink-0" />
              <span>{brandBadge}</span>
            </div>
            <div className="h-px w-8 sm:w-12 bg-gradient-to-l from-transparent to-indigo-500/40" />
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tight text-white mb-8 leading-tight">
            メモを投げ込むだけ。<br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400 whitespace-nowrap">
              AIと育てる、第二の脳。
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            {heroLead}
          </p>

          <div id="waitlist" className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={WAITLIST_FORM_URL} onClick={() => handleCtaClick('waitlist_hero')} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-4 rounded-xl font-bold transition shadow-lg shadow-indigo-500/25 whitespace-nowrap">
                先行アクセスに登録 <ArrowRight size={18} />
              </a>
              <a href="/demo" onClick={() => handleCtaClick('demo_hero')} className="flex items-center justify-center gap-2 bg-slate-900 border border-slate-700 hover:border-indigo-500 text-slate-200 px-8 py-4 rounded-xl font-bold transition whitespace-nowrap">
                デモを試す <ArrowRight size={18} />
              </a>
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">※現在クローズドベータ版を開発中です。WaitlistはGoogle Formで開きます。<br />登録にあたっては<a href="/privacy" onClick={() => handleLegalClick('privacy')} className="underline hover:text-indigo-400">プライバシーポリシー</a>をご確認ください。</p>
          </div>
        </div>
      </section>

      <section id="think" ref={thinkRef} className="py-24 bg-slate-950">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold tracking-wider mb-6 uppercase">
              Value : Think
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">思考を辿って、考える（Think）</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
              Sinkの目的は情報を単に整理することではありません。<br />
              あなたのAssets（知的資産）とともに、思考の出発点を広げることです。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">

            <div className="bg-slate-900 rounded-2xl border border-slate-800 shadow-xl transition duration-300 group flex flex-col overflow-hidden">
              <div className="h-56 bg-slate-950/80 border-b border-slate-800/50 relative flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-40 group-hover:opacity-80 transition-opacity duration-700">
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <line x1="50%" y1="50%" x2="25%" y2="30%" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="4 2" />
                    <line x1="50%" y1="50%" x2="75%" y2="25%" stroke="#6366f1" strokeWidth="2" opacity="0.8" />
                    <line x1="50%" y1="50%" x2="80%" y2="70%" stroke="#10b981" strokeWidth="1.5" />
                    <circle cx="25%" cy="30%" r="6" fill="#818cf8" />
                    <text x="25%" y="24%" textAnchor="middle" fill="#ffffff" fontSize="10" className="font-mono">ノート・メモの活用法の調査結果</text>
                    <circle cx="75%" cy="25%" r="10" fill="#6366f1" />
                    <text x="75%" y="16%" textAnchor="middle" fill="#ffffff" fontSize="10" className="font-mono">AI時代の情報管理のコツ</text>
                    <circle cx="80%" cy="70%" r="8" fill="#10b981" />
                    <text x="80%" y="82%" textAnchor="middle" fill="#1fffb4" fontSize="10" className="font-mono">第二の頭脳と知識の状態</text>
                    <circle cx="50%" cy="50%" r="14" fill="#4f46e5" className="animate-pulse" />
                  </svg>
                </div>
                <div className="absolute top-1/2 left-1/2 bg-indigo-600 text-white font-medium text-xs px-3 py-1.5 rounded-lg shadow-lg transform -translate-x-1/2 -translate-y-1/2 mt-8">
                  #知識の管理
                </div>
              </div>
              <div className="p-8 flex-1">
                <div className="w-12 h-12 bg-blue-900/30 text-blue-400 rounded-xl flex items-center justify-center mb-6 border border-blue-500/30">
                  <Share2 size={24} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Think Mapで俯瞰する</h3>
                <p className="text-slate-400 leading-relaxed">
                  蓄積されたAsset、Noteの繋がりを、地図・ネットワークのように視覚化。<br />
                  俯瞰することで、無関係に見えた思考・知識の間に「思わぬ結びつき」を発見し、アイデアや思考の地図を広げます。
                </p>
              </div>
            </div>
            <div className="bg-slate-900 rounded-2xl border border-slate-800 shadow-xl transition duration-300 group flex flex-col overflow-hidden">
              <div className="h-56 bg-slate-950/80 border-b border-slate-800/50 relative flex items-end justify-center p-4 overflow-hidden">
                <div className="w-full max-w-sm space-y-4 opacity-80 group-hover:opacity-100 transition-opacity duration-500 transform">
                  <div className="flex justify-end">
                    <div className="bg-indigo-600 text-white text-xs px-4 py-2.5 rounded-2xl rounded-tr-sm max-w-[85%] shadow-md">
                      この新しいプロジェクト、どこから手をつけるべきか迷ってる。
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-slate-800 border border-slate-700 text-slate-300 text-xs px-4 py-3 rounded-2xl rounded-tl-sm max-w-[95%] shadow-md relative">
                      <div className="flex items-center gap-1.5 mb-2 text-[10px] text-emerald-400 font-medium bg-emerald-900/20 inline-flex px-2 py-0.5 rounded border border-emerald-800/50">
                        <Database size={10} /> 過去の「意思決定Stance」を参照
                      </div>
                      <p className="leading-relaxed">
                        以前あなたは<span className="text-indigo-300">「スピードよりも、過程の透明性を重視する」</span>と結論づけています。まずはプロセスを再整理して、全員の認識を揃えみませんか？
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex-1">
                <div className="w-12 h-12 bg-purple-900/30 text-purple-400 rounded-xl flex items-center justify-center mb-6 border border-purple-500/30">
                  <Bot size={24} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">第二の自分と対話する</h3>
                <p className="text-slate-400 leading-relaxed">
                  AssetやNoteを知識として、あなたの過去の判断基準や好みを理解したAI Buddyと対話が可能です。<br />
                  AI Buddyだけでなく、あなたの性格や考え方の傾向と逆の思考をし、批判的な側面を持つAnti Buddyとも対話できます。
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="sink" ref={sinkRef} className="py-24 bg-slate-900 border-y border-slate-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-bold tracking-wider mb-6 uppercase">
                Capture : Sink
              </div>
              <h3 className="text-3xl font-bold text-white mb-6">鮮やかなうちに、沈める（Sink）</h3>
              <p className="text-slate-400 mb-8 leading-relaxed text-lg">
                整理のことは後回し。<br />
                スマホのチャットから、走り書きやURL、アイデアをそのまま投げ込むだけ。
                「どこに書こう」「整理しなきゃ」という負担が、思考を止めることはありません。
              </p>
              <ul className="space-y-4">
                {['スマホから即座にSinkへ投げ込む',
                  'AIがメモを読みやすくNoteにまとめる',
                  '一次情報を失わない厳格なデータ保持'].map((text) => (
                    <li key={text} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="text-emerald-400" size={20} />
                      <span>{text}</span>
                    </li>
                  ))}
              </ul>
            </div>
            <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 shadow-2xl">
              <div className="space-y-4">
                <div className="bg-slate-800 p-4 rounded-xl ml-8">
                  <p className="text-sm text-slate-200">効率化より、ユーザーの納得感が重要。納得感の要素は「シンプルさ」「即時性」「信頼性」でまず整理してみる…</p>
                </div>
                <div className="bg-slate-800 p-4 rounded-xl ml-8">
                  <p className="text-sm text-slate-200">シンプルさ： UI/UXだけでなくシステム面も含む。AI駆動開発・運用なら…</p>
                </div>
                <div className="bg-slate-800 p-4 rounded-xl ml-8">
                  <p className="text-sm text-slate-200">即時性： フリクションレスさが肝要。ユーザの判断≒負荷を最小限に…</p>
                </div>
                <div className="bg-slate-800 p-4 rounded-xl ml-8">
                  <p className="text-sm text-slate-200">信頼性： 取り扱いデータ、サービスの再確認がToDo。昨今最も難しい…</p>
                </div>
                <div className="text-center">
                  <span className="text-[10px] bg-slate-800 text-slate-500 px-2 py-1 rounded animate-pulse">Noteまとめ実行中...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="sync" ref={syncRef} className="py-24 bg-slate-950">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1 bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl relative overflow-hidden min-h-[300px] flex flex-col">
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/10 to-transparent" />
              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-950 rounded border border-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs text-slate-400 font-mono">Sync : Hint Review</span>
                  </div>
                  <span className="text-[10px] bg-emerald-900/30 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/20">New Candidate</span>
                </div>

                <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 shadow-inner">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0 border border-emerald-500/30">
                      <Sparkles size={16} className="text-emerald-400" />
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-white font-bold font-mono">シンプルさへの追及</div>
                      <div className="text-xs text-slate-400 font-mono">#シンプルさ #フリクションレス #UIUX #デザイン思考</div>
                    </div>
                  </div>
                  <div className="space-y-2 pl-11">
                    <div className="text-sm text-slate-300 leading-relaxed font-mono">即時性、フリクションレス性はシンプルさの実現により…</div>
                  </div>
                </div>

                <div className="flex gap-2 pl-11">
                  <div className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-bold">採用してAsset化</div>
                  <div className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-400 text-[10px]">Reject</div>
                </div>

                <div className="pt-4 border-t border-slate-800">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={12} className="text-slate-500" />
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Source Note</span>
                  </div>
                  <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-3">
                    <div className="text-xs text-slate-400 font-mono">20260210_note_new_service_launch...</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold tracking-wider mb-6 uppercase">
                Review : Sync
              </div>              <h3 className="text-3xl font-bold text-white mb-6">あなたの芯に、繋ぐ<br />（Sync）</h3>
              <p className="text-slate-400 mb-8 leading-relaxed text-lg">
                AIがノートからあなたの考え方、知識、自己矛盾、見落としていた論点をHint（思考の手がかり）として提示。
                あなたが選び取ることで、確かなAssetへと育ちます。
              </p>
              <ul className="space-y-4">
                {[
                  'AIがバラバラなNoteからHint（思考の手がかり）を見つける',
                  'Assetは「判断基準」「嗜好」「ToDo」「問い」…といったラベルで管理',
                  'あなた自身がAssetとして選ぶことで、考え・知識を「知的資産」へ'
                ].map((text) => (<li key={text} className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="text-emerald-400" size={20} />
                  <span>{text}</span>
                </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="process" ref={processRef} className="py-24 bg-slate-900 border-y border-slate-800">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">複雑な手順はいらない。</h2>
          <p className="text-slate-400 text-lg leading-relaxed mb-16 max-w-3xl mx-auto">
            環境構築や複雑なルールの学習は不要です。<br className="hidden md:block" />
            3ステップで、あなたの「垂れ流していた思考」を、一生使える知的資産へと整えます。
          </p>

          <div className="grid md:grid-cols-3 gap-6 relative">
            {/* Flow arrows for desktop */}
            <div className="hidden md:block absolute top-1/2 left-[28%] w-[10%] h-px bg-slate-700 -translate-y-1/2 z-0" />
            <div className="hidden md:block absolute top-1/2 left-[62%] w-[10%] h-px bg-slate-700 -translate-y-1/2 z-0" />

            <div className="relative z-10 bg-slate-950 p-8 rounded-2xl border border-slate-800 flex flex-col items-center shadow-lg">
              <div className="w-12 h-12 bg-indigo-900/30 text-indigo-400 rounded-full flex items-center justify-center mb-6 border border-indigo-500/20">
                <MessageSquare size={24} />
              </div>
              <h4 className="text-white font-bold mb-3">1. Sink (沈める)</h4>
              <p className="text-sm text-slate-400">スマホからチャット形式で即座にメモ。<br />AIがNoteをまとめ、読み返せる形にします。</p>
            </div>

            <div className="relative z-10 bg-slate-950 p-8 rounded-2xl border border-indigo-500/20 flex flex-col items-center shadow-lg">
              <div className="w-12 h-12 bg-emerald-900/30 text-emerald-400 rounded-full flex items-center justify-center mb-6 border border-emerald-500/20">
                <Sparkles size={24} />
              </div>
              <h4 className="text-white font-bold mb-3">2. Sync (繋ぐ)</h4>
              <p className="text-sm text-slate-400">AIが抽出した「Hint」をチェック。<br />Assetにして確かなあなたの知的資産へ。</p>
            </div>

            <div className="relative z-10 bg-slate-950 p-8 rounded-2xl border border-slate-800 flex flex-col items-center shadow-lg">
              <div className="w-12 h-12 bg-purple-900/30 text-purple-400 rounded-full flex items-center justify-center mb-6 border border-purple-500/20">
                <BrainCircuit size={24} />
              </div>
              <h4 className="text-white font-bold mb-3">3. Think (考える)</h4>
              <p className="text-sm text-slate-400">蓄積したAssetを持つAIバディと壁打ち。<br />Assetの繋がりから思考を広げます。</p>
            </div>
          </div>
        </div>
      </section>

      <section id="final_cta" ref={finalCtaRef} className="py-24 relative overflow-hidden bg-slate-950">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">思考を流さず、<br className="block md:hidden" />育てよう。</h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            あなたの思考・情報が流れていくだけの日常を終わりにしませんか。
          </p>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            デモを触って、メモが知的資産へ育つ流れを確認してみてください。
          </p>

          <div className="max-w-xl mx-auto flex flex-col sm:flex-row gap-3 justify-center">
            <a href={WAITLIST_FORM_URL} onClick={() => handleCtaClick('waitlist_bottom')} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-4 rounded-xl font-bold transition shadow-lg shadow-indigo-500/25 whitespace-nowrap">
              先行アクセスに登録 <ArrowRight size={18} />
            </a>
            <a href="/demo" onClick={() => handleCtaClick('demo_bottom')} className="flex items-center justify-center gap-2 bg-slate-900/80 border border-slate-700 hover:border-indigo-500 text-slate-200 px-8 py-4 rounded-xl font-bold transition backdrop-blur-sm whitespace-nowrap">
              デモを試す <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 py-12 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2 text-slate-400">
            <BrainCircuit size={20} />
            <span className="font-bold">{brandName}</span>
          </div>
          <div className="flex gap-6 text-sm text-slate-500">
            <a href="/privacy" onClick={() => handleLegalClick('privacy')} className="hover:text-white transition">プライバシーポリシー</a>
            <a href="/terms" onClick={() => handleLegalClick('terms')} className="hover:text-white transition">利用規約</a>
            <a href="/tokushoho" onClick={() => handleLegalClick('tokushoho')} className="hover:text-white transition">特定商取引法に基づく表記</a>
          </div>
          <div className="text-sm text-slate-500">
            &copy; 2026 {brandName}. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
